<template>
  <section :id="section.id" class="bg-white border border-gray-200 rounded-2xl p-6">
    <h2 class="font-semibold text-[#1E293B] text-lg mb-3">{{ section.heading }}</h2>

    <ol v-if="section.steps?.length" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 text-center">
      <li v-for="step in section.steps" :key="step.label" class="rounded-xl border p-2"
          :class="step.state === 'done' ? 'border-green-200 bg-green-50' : step.state === 'today' ? 'border-blue-200 bg-blue-50' : 'border-gray-100'">
        <span class="block text-[11px] text-[#64748B]">{{ step.label }}</span>
        <span class="block text-sm font-semibold text-[#1E293B]">{{ step.date || '—' }}</span>
      </li>
    </ol>

    <div class="space-y-2 text-sm text-[#334155] leading-relaxed">
      <p v-for="(p, i) in section.paragraphs" :key="i">{{ p }}</p>
    </div>

    <dl v-if="section.items?.length" class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
      <div v-for="[label, value] in section.items" :key="label" class="flex justify-between gap-4 border-b border-gray-50 py-1">
        <dt class="text-[#64748B]">{{ label }}</dt><dd class="font-medium text-[#1E293B] text-right">{{ value }}</dd>
      </div>
    </dl>

    <table v-if="section.latest?.breakdown_consistent" class="w-full text-sm mt-4">
      <thead><tr class="text-left text-xs text-[#64748B] border-b"><th class="py-2">Category</th><th class="py-2 text-right">Subscribed (as of {{ section.latest.as_of }})</th></tr></thead>
      <tbody>
        <template v-for="[k, l] in [['qib','QIB'],['nii','NII'],['retail','Retail'],['employee','Employee'],['total','Overall']]" :key="k">
          <tr v-if="section.latest[k] != null" class="border-b border-gray-50">
            <td class="py-1.5">{{ l }}</td><td class="py-1.5 text-right font-medium">{{ times(section.latest[k]) }}</td>
          </tr>
        </template>
      </tbody>
    </table>

    <div v-if="section.table?.length" class="overflow-x-auto mt-4">
      <table class="w-full text-sm">
        <thead><tr class="text-left text-xs text-[#64748B] border-b"><th class="py-2">Period</th><th class="py-2 text-right">Revenue</th><th class="py-2 text-right">Profit</th><th class="py-2 text-right">Assets</th><th class="py-2 text-right">Net worth</th></tr></thead>
        <tbody>
          <tr v-for="r in section.table" :key="r.year" class="border-b border-gray-50">
            <td class="py-1.5">{{ r.year }}</td>
            <td v-for="k in ['revenue','profit','assets','net_worth']" :key="k" class="py-1.5 text-right">{{ num(r[k]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <ul v-if="section.notes?.length" class="mt-4 space-y-1.5 text-sm bg-amber-50 border border-amber-100 rounded-xl p-4 text-amber-900 list-disc list-inside">
      <li v-for="(n, i) in section.notes" :key="i">{{ n }}</li>
    </ul>

    <ul v-if="section.links?.length" class="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm">
      <li v-for="[label, url] in section.links" :key="url">
        <a v-if="url.startsWith('http')" :href="url" target="_blank" rel="noopener nofollow" class="text-[#2563EB] hover:underline">{{ label }} ↗</a>
        <router-link v-else :to="url" class="text-[#2563EB] hover:underline">{{ label }} →</router-link>
      </li>
    </ul>
  </section>
</template>

<script setup>
// Mirrors resources/views/ssr/partials/narrative-section.blade.php.
import { times, isNum } from '../format';

defineProps({ section: { type: Object, required: true } });
const num = (v) => isNum(v) ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';
</script>
